import * as React from 'react';
import assign from 'object-assign';
import HubItem from './HubItem';
import { BasicHubItemProps, HubItemIconProps, HubItemTextProps } from './HubItem.types';
import { observer } from 'mobx-react-lite';
import { store } from 'mini-theme/lib/store/store';
import { Toggle } from 'fabric-toggle';
import {
    ACCESSIBILITY_BREAKING_BACKGROUND_COLORS,
    DEFAULT_DARK_MODE_COLOR,
    DEFAULT_THEME_PRIMARY_COLOR,
    WHITE_BACKGROUND_COLOR,
} from 'mini-sass-mixins/lib/js/colorConstants';

const styles = require('./ToggleHubItem.scss');

export interface ToggleHubItemProps extends BasicHubItemProps, HubItemIconProps, HubItemTextProps {
    defaultChecked?: boolean;
    color?: string;
    /**
     * Prop to change the checked property of the toggle based on external changes
     */
    isChecked?: boolean;
    /**
     * If this value is set to true, clicking on the toggle does not change the state of toggle
     * This means that toggle is controlled by `isChecked` property
     */
    useCheckedStateFromProps?: boolean;

    /**
     * Classname for toggle button
     */
    toggleButtonClassName?: string;
}

/*In the current versions of mobx-react/ mobx-react-lite, observers being wrapped over React.forwardRef is not allowed. Please refer https://stackoverflow.com/questions/64227496/fowarding-a-ref-with-mobx*/
const ToggleHubItem = observer(
    (props: ToggleHubItemProps): JSX.Element => {
        const {
            useCheckedStateFromProps,
            isChecked,
            defaultChecked,
            toggleButtonClassName,
            styles: stylesFromProps = {},
            ref,
        } = props;

        let { color } = props;
        let toggleColors = null;

        if (store.darkMode) {
            color = color ?? DEFAULT_THEME_PRIMARY_COLOR;
            toggleColors = color;
        }

        const checkedBackgroundColor =
            color && ACCESSIBILITY_BREAKING_BACKGROUND_COLORS.indexOf(color.toUpperCase()) >= 0
                ? WHITE_BACKGROUND_COLOR
                : color;

        const renderToggle = () => (
            <>
                <span className={stylesFromProps.primaryTextStyles}>{props.primaryText}</span>
                <Toggle
                    className={toggleButtonClassName}
                    defaultChecked={defaultChecked}
                    text={''}
                    onChanged={null /*The upper level hubItem will handle this*/}
                    isChecked={isChecked}
                    useCheckedStateFromProps={useCheckedStateFromProps}
                    isForcedAriaHidden
                    toggleStyles={{
                        checkedBackgroundColor: checkedBackgroundColor,
                        checkedBorderColor: color,
                        uncheckedBackgroundColor: store.darkMode
                            ? DEFAULT_DARK_MODE_COLOR
                            : toggleColors,
                        checkedThumbColor: toggleColors,
                    }}
                />
            </>
        );

        const stylesToApply = assign(
            {},
            {
                className: styles.itemContainer,
                textContainerStyles: styles.textContainer,
            },
            stylesFromProps
        );

        return (
            <HubItem
                forwardRef={ref}
                ariaPressed={isChecked}
                {...props}
                styles={stylesToApply}
                onRenderText={renderToggle}
            />
        );
    }
);

export default ToggleHubItem;
